import { useManager } from 'dreamstate';
import React, { useCallback, useEffect, useRef } from 'react';

import { Flex } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Maybe, Optional, Callable } from '@vimeo/core/types/generic';
import { ECredentialsType } from '@vimeo/live-core/api/interaction';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { interactionToolsConfig } from '@vimeo/live-core/configs/InteractionToolsConfig';
import {
  IInteractionSessionContext,
  InteractionSessionManager,
} from '@vimeo/live-core/store/interaction/session';
import {
  EInteractionAuthOption,
  EInteractionRoomPrivacy,
} from '@vimeo/live-core/types/interaction_room';
import { EInteractionModule } from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';
import { Logger, useLogger } from '@vimeo/logging';
import { IChannelMessage, useBroadcastChannel } from '@vimeo/use-broadcast-channel';

import { createInteractionDomName } from '../../../../../libs/shared/utils/dom/identifying';
import { InteractionAutoLayout } from '../auto/InteractionAutoLayout';
import { PAGE_WRAPPER_STYLE } from '../styles';
import { InteractionContent } from './InteractionContent';

interface IInteractionRoomLayoutProps extends IBaseDomElementProps {
  interactionSessionContext?: IInteractionSessionContext;
}

/**
 * Generic layout of the room based on provided props / settings.
 */
export function InteractionRoomLayout({
  id = createInteractionDomName('widget-room'),
  interactionSessionContext: {
    sessionId,
    roomInfo: { value: roomInfo },
    roomUser: { value: roomUser },
    roomPassword,
    roomHashedPassword,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionSessionManager),
}: IInteractionRoomLayoutProps) {
  const log: Logger = useLogger('📡 BC in InteractionRoomLayout');

  const {
    initialState: { interaction },
  } = useLiveGlobals<IInteractionStoreInitialState>();

  const askDataUnsubscriberRef = useRef<Optional<Callable>>(null);

  const isManagementAccessed: boolean = Boolean(
    roomUser?.isCreator && !interaction?.feature?.isManagementDisabled,
  );
  const authOptions: Record<EInteractionAuthOption, boolean> =
    roomInfo?.settings.authOptions || ({} as Record<EInteractionAuthOption, boolean>);

  const isRoomPasswordProtected: boolean = Boolean(
    roomInfo?.settings.privacy === EInteractionRoomPrivacy.PASSWORD,
  );
  const isRegistrantFlow: boolean = Boolean(
    authOptions[EInteractionAuthOption.REGISTRANT] === true,
  );

  const activeModule: Maybe<EInteractionModule> = interaction?.feature?.module;
  const isAutoLayout: boolean = !Boolean(activeModule) || activeModule === EInteractionModule.AUTO;

  const { isChannelActive, sendChannelMessage, subscribe, closeChannel } = useBroadcastChannel({
    name: interactionToolsConfig.BROADCAST_CHANNEL.NAME,
  });

  const onUnsubscribeFromAskData = useCallback(() => {
    if (askDataUnsubscriberRef.current) {
      log.info('Unsubscribe from ask_data messages');

      askDataUnsubscriberRef.current();
    }
  }, [log]);

  const onSubscribeToAskData = useCallback(() => {
    if (!askDataUnsubscriberRef.current) {
      askDataUnsubscriberRef.current = subscribe(function subscribeToAskData(
        message: IChannelMessage,
      ) {
        log.info('Subscribe to ask_data messages');

        if (
          message.type !== interactionToolsConfig.BROADCAST_CHANNEL.MESSAGE_TYPE.ASK_DATA ||
          String(message?.payload?.sessionId) !== String(sessionId)
        ) {
          return;
        }

        log.info('Receive ask_data message:', message);

        const newData: IChannelMessage = {
          type: interactionToolsConfig.BROADCAST_CHANNEL.MESSAGE_TYPE.NEW_DATA,
          payload: {
            sessionId: String(sessionId),
            type: message.payload.type,
          },
        } as IChannelMessage;

        if (message.payload.type === ECredentialsType.PASSWORD && roomPassword) {
          newData.payload.value = roomPassword;
        }

        if (message.payload.type === ECredentialsType.HASHED_PASSWORD && roomHashedPassword) {
          newData.payload.value = roomHashedPassword;
        }

        if (message.payload.type === 'registrant_uuid' && roomUser?.id) {
          newData.payload.value = roomUser.id;
        }

        if (newData.payload.value) {
          log.info('Post new_data message as a response', newData);

          sendChannelMessage(newData);
        }
      });
    }
  }, [
    log,
    roomHashedPassword,
    roomPassword,
    roomUser?.id,
    sendChannelMessage,
    sessionId,
    subscribe,
  ]);

  useEffect(() => {
    if (!isChannelActive) {
      return;
    }

    if (isRoomPasswordProtected || isRegistrantFlow) {
      onSubscribeToAskData();
    } else {
      onUnsubscribeFromAskData();
    }
  }, [
    isChannelActive,
    isRegistrantFlow,
    isRoomPasswordProtected,
    onSubscribeToAskData,
    onUnsubscribeFromAskData,
  ]);

  useEffect(() => {
    return () => {
      onUnsubscribeFromAskData();
      log.info('Close channel');
      closeChannel();
    };
  }, [log, onUnsubscribeFromAskData, closeChannel]);

  return isAutoLayout ? (
    <InteractionAutoLayout />
  ) : (
    <Flex id={id} sx={PAGE_WRAPPER_STYLE}>
      <InteractionContent
        id={createInteractionDomName(id, 'content')}
        activeModule={activeModule}
        isManagementAccessed={isManagementAccessed}
      />
    </Flex>
  );
}
